import h from 'hyperscript';
import './hierarchy-selection.css';
import 'ui/form-group/form-group';
import { deleteUrlParam, setParam } from 'cadenza/utils/url-param-utils';
import { HIERARCHY_PARAM_PREFIX } from 'cadenza/api-client/processing-param-util';
import { Select } from 'ui/select/select';

import i18n from './hierarchy-selection.properties';
import { HIERARCHY_CHANGE, HIERARCHY_RESET } from './event-types';

const NAME = 'd-hierarchy-selection';

export class HierarchySelection extends HTMLElement {

  connectedCallback () {
    this.classList.add(NAME);
    const {
      hierarchies
    } = this.previousElementSibling.value;
    this.controlComponents = this._getControlComponents(hierarchies);
    this.controlComponents.forEach(controlComponent => {
      this._updateResetButtonVisibility(controlComponent);
      controlComponent.reset = () => {
        this._resetHierarchy(controlComponent.dSelect);
        this._updateResetButtonVisibility(controlComponent);
      };
      this.append(controlComponent);
    });
    this.classList.add('hierarchy-selection');
    window.resetHierarchies = () => {
      this.controlComponents.forEach(controlComponent => controlComponent.reset());
    };
  }

  get dirty () {
    return this.controlComponents.some(controlComponent => this._isDirty(controlComponent));
  }

  _isDirty (controlComponent) {
    const select = controlComponent.dSelect;
    return getIdOfSelection(select) !== select.hierarchy.persistedLevel;
  }

  get disabled () {
    return !!this.getAttribute('disabled');
  }

  _getControlComponents (hierarchies = []) {
    return hierarchies.map(hierarchy => {
      const select = new Select({
        dropdownParent: this,
        data: extractOptions(hierarchy.levels, hierarchy.selectedLevel, hierarchy.persistedLevel)
      });
      select.disabled = this.disabled;
      select.hierarchy = hierarchy;
      select.addEventListener('change', event => this._onChange(event));

      const resetButton = h('button.d-anchor.d-hierarchy--reset.hidden.qa-reset-hierarchy-button', {
        onclick: () => {
          this._resetHierarchy(select);
        }
      }, i18n('reset'));
      const controlComponent = h('div.hierarchy-wrapper',
        h('label.d-condition--name', hierarchy.printName),
        resetButton,
        select);
      controlComponent.dSelect = select;
      if (!hierarchy.visible) {
        controlComponent.classList.add('hidden');
      }
      return controlComponent;
    });
  }

  _onChange (event) {
    const changedSelectElement = event.currentTarget;
    this._onSelectedElementChange(changedSelectElement);
  }

  _onSelectedElementChange (changedSelectElement) {
    const name = changedSelectElement.hierarchy.name;
    const paramName = HIERARCHY_PARAM_PREFIX + name;
    const paramValue = getIdOfSelection(changedSelectElement);
    setParam({ paramName, paramValue });
    this._notify(HIERARCHY_CHANGE, {
      name,
      selectedLevel: paramValue
    });
    changedSelectElement.hierarchy.selectedLevel = paramValue;
    this._updateResetButtonVisibility(changedSelectElement.parentElement);
  }

  _resetHierarchy (select, fast = false) {
    select.hierarchy.selectedLevel = select.hierarchy.persistedLevel;
    select.selection = select.hierarchy.persistedLevel;
    // all hierarchies.
    deleteUrlParam(HIERARCHY_PARAM_PREFIX + select.hierarchy.name);
    this._onSelectedElementChange(select);
    this._updateResetButtonVisibility(select.parentElement);
    if (fast) {
      return;
    }
    this._notify(HIERARCHY_RESET, {
      name: select.hierarchy.name
    });
  }

  resetAllHierarchies (fast = false) {
    const dirtySelectElements = this.controlComponents
      .map(controlComponent => controlComponent.dSelect)
      .filter(select => getIdOfSelection(select) !== select.hierarchy.persistedLevel);
    for (const select of dirtySelectElements) {
      this._resetHierarchy(select, fast);
    }
    return dirtySelectElements.map(select => select.hierarchy.name);
  }

  // Notifies the orchestrator that something changed in this component
  _notify (eventType, data) {
    this.dispatchEvent(new CustomEvent(eventType, { detail: data, bubbles: true }));
  }

  isIncludedInCurrentlySelectedHierarchyLevel (hierarchyName, hierarchyLevelName) {
    const accordingHierarchySelect = findAccordingControlComponent(this.controlComponents, hierarchyName);
    if (!accordingHierarchySelect) {
      return false;
    }
    const accordingSelect = accordingHierarchySelect.dSelect;
    const hierarchy = accordingSelect.hierarchy;
    const levelToCheckIndex = hierarchy.levels.findIndex((sel) => sel.name === hierarchyLevelName);
    if (levelToCheckIndex < 0) {
      return false;
    }
    const selectedLevelIndex = hierarchy.levels.findIndex((sel) => sel.name === hierarchy.selectedLevel);
    return levelToCheckIndex <= selectedLevelIndex;
  }

  updateHierarchySelectVisibility (predicate) {
    let anyVisible = false;
    this.controlComponents.forEach(controlComponent => {
      const hierarchyName = controlComponent.dSelect.hierarchy.name;
      const visible = predicate(hierarchyName);
      controlComponent.classList.toggle('hidden', !visible);
      anyVisible = anyVisible || visible;
    });
    this.parentElement.classList.toggle('hidden', !anyVisible);
  }

  _updateResetButtonVisibility (controlComponent) {
    const resetButton = controlComponent.querySelector('.d-hierarchy--reset');
    resetButton.classList.toggle('hidden', this.disabled || !this._isDirty(controlComponent));
  }

}

function findAccordingControlComponent (controlComponents, hierarchyName) {
  if (!Array.isArray(controlComponents) || controlComponents.length === 0) {
    return undefined;
  }
  return controlComponents.find((controlComponent) => {
    const select = controlComponent.dSelect;
    if (!select || !select.hierarchy || select.hierarchy.name !== hierarchyName) {
      return false;
    }
    return true;
  });
}

function extractOptions (levelOptions = [], selectedLevel) {
  return levelOptions.map(levelOption => ({
    text: levelOption.printName,
    id: levelOption.name,
    selected: levelOption.name === selectedLevel
  }));
}

/*
 * Returns the ID of the selected element.
 */
function getIdOfSelection (select) {
  return select.selection[0].id;
}

customElements.define(NAME, HierarchySelection);
